import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "app-header-wrapper" }
const _hoisted_2 = { class: "app-header__section app-header__section--left" }
const _hoisted_3 = {
  key: 0,
  class: "app-header__section app-header__section--center"
}
const _hoisted_4 = ["data-testid"]
const _hoisted_5 = { class: "app-header__page-cta-label" }
const _hoisted_6 = {
  key: 1,
  class: "app-header__section app-header__section--right"
}

import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import { useStore } from 'vuex';
import NotificationComponent from './NotificationComponent.vue';
import HeaderMobileDrawer from './HeaderMobileDrawer.vue';
import SvgLogo from '../common/SvgLogo.vue';
import { useNotifications } from '../../../store/notifications';
import { getSafeAvatarUrl } from '@/shared/lib/avatar';
import LogoutConfirmDialog from '../common/LogoutConfirmDialog.vue';
import ProfileMenu from './ProfileMenu.vue';
import UserAvatar from '../common/UserAvatar.vue';
import UserRoleChips from '../common/UserRoleChips.vue';
import { useMentorApplicationStatus, type Phase } from '@/composables/useMentorApplicationStatus';
import { useSellingReadiness } from '@/composables/useSellingReadiness';
import { headerCta } from '@/shared/lib/headerCta';


export default /*@__PURE__*/_defineComponent({
  __name: 'HeaderDefault',
  setup(__props) {

/* eslint-disable */
const route = useRoute();
const router = useRouter();
const store = useStore();
const { mdAndDown, smAndDown } = useDisplay();
const notifications = useNotifications();

const mobileDrawer = ref(false);
const showLogoutConfirm = ref(false);
const logoutLoading = ref(false);
const profileMenuOpen = ref(false);

const isMobile = computed(() => mdAndDown.value);

// Мобильный знак меньше десктопного — шапка там уже. Под кнопку страницы знак
// больше не ужимается: секции наконец делят строку через flex, а не внахлёст
// (см. media-запрос ≤640px), и подбирать ширину скриптом незачем.
const logoWidth = computed<number>(() => (isMobile.value ? 112 : 140));
// Компактный режим чипа роли — только на узком вьюпорте (<960px), где суффикс
// чипа скрыт по ширине (см. `.app-header__role-chips--mobile` media-query).
// Именно здесь payout-missing нужно нести янтарной точкой + tap→tooltip.
const chipCompact = computed(() => smAndDown.value);
const isAuthenticated = computed<boolean>(() => store.getters.isAuthenticated);

const user = computed(() => store.state.user);
const guestLabel = computed(() => {
    const id = user.value?.id;
    return `Гость${id ? ` #${id}` : ''}`;
});
const userName = computed(() => {
    if (!isAuthenticated.value) return guestLabel.value;
    const first = user.value?.first_name?.trim();
    const last = user.value?.last_name?.trim();
    const username = user.value?.username?.trim();
    return [first, last].filter(Boolean).join(' ') || username || guestLabel.value;
});
const userEmail = computed(() => (isAuthenticated.value ? (user.value?.email ?? '') : ''));
const avatarUrl = computed(() => getSafeAvatarUrl(user.value?.avatar_url));
const userRole = computed(() => store.getters.userRole as string | null);
const canBecomeMentor = computed(() => userRole.value !== 'mentor');

// Гостю на мобиле в правом кластере не остаётся ничего: вход из шапки убран
// (он есть в футере), а колокол и профиль — только авторизованным. Пустую
// flex-секцию не рендерим вовсе: она забирала бы себе зазор между секциями.
const hasRightCluster = computed<boolean>(() => isAuthenticated.value || !isMobile.value);

// Issue #8 — статус наставник-заявки для chip'а в header.
// Composable сам подтягивает GET /mentor-applications/me/ на mount.
const mentorAppStatus = useMentorApplicationStatus();
const mentorAppPhase = computed<Phase>(() => mentorAppStatus.phase.value);
const mentorAppRejectionReason = computed<string>(() => mentorAppStatus.rejectionReason.value);

// CTA «Стать наставником» — только пока заявки нет вовсе (issue #8) и только
// на десктопе: на мобиле центр шапки отдан действию страницы.
const showBecomeMentorCta = computed<boolean>(
    () =>
        isAuthenticated.value &&
        !isMobile.value &&
        canBecomeMentor.value &&
        mentorAppPhase.value === 'none',
);

// Пустой центр всё равно остаётся flex-элементом и съедает зазоры вокруг себя,
// а на узкой шапке лишние 24px — заметная потеря.
const hasCenterContent = computed<boolean>(
    () => headerCta.value !== null || showBecomeMentorCta.value,
);

// 2026-06-01: payout-missing визуализируется прямо внутри пилюли «Наставник»
// вместо отдельной sticky-полосы (которая занимала целую строку и раздражала).
// Источник истины — selling_readiness; approved_no_payout ⇒ карты нет.
const { value: sellingReadiness } = useSellingReadiness();
const payoutMissing = computed<boolean>(() => sellingReadiness.value === 'approved_no_payout');

function onMentorChipClick(phase: Phase): void {
    // none/draft/rejected/changes_requested → /become-mentor
    // pending → no-op (заявка уже на проверке, идти некуда)
    // approved → профиль наставника
    if (phase === 'pending') return;
    if (phase === 'approved') {
        handleNavigate('/dashboard');
        return;
    }
    handleNavigate('/become-mentor');
}

function onPayoutAttachClick(): void {
    handleNavigate('/ProfileSettings?tab=payment');
}

// NB: здесь жил `drawerNavItems` с бейджем/подсветкой календаря, который
// передавался в <HeaderMobileDrawer :nav-items>. Но HeaderMobileDrawer этот
// проп ИГНОРИРУЕТ (рендерит только <ProfileMenu/>), так что бейдж нигде не
// отрисовывался — это был мёртвый дубль живого индикатора в доке (`app-dock`
// в App.vue, который читает ту же `store.calendarSummary`). Удалено вместе с
// зависимостями (baseDrawerNav / mentorsCatalogEnabled / eventsCatalogEnabled /
// calendar*-computed). Живой календарь-индикатор — в App.vue.

const handleNavigate = (path: string) => {
    mobileDrawer.value = false;
    router.push(path);
};

const handleLogout = async () => {
    mobileDrawer.value = false;
    showLogoutConfirm.value = true;
};

const confirmLogout = async () => {
    logoutLoading.value = true;
    try {
        await store.dispatch('logout');
        router.push('/login');
    } catch (error) {
        // Даже если выход упал (сеть/бэкенд), чистим локальное состояние и уводим
        // на /login — иначе пользователь остаётся в приватном разделе. Тот же
        // паттерн, что в ProfileMenu.vue.
        console.error('Ошибка при выходе:', error);
        store.commit('clearUser');
        router.push('/login');
    } finally {
        logoutLoading.value = false;
        showLogoutConfirm.value = false;
    }
};

const toggleDrawer = () => {
    mobileDrawer.value = !mobileDrawer.value;
};

watch(
    () => route.path,
    () => {
        mobileDrawer.value = false;
    },
);

const loadNotificationsIfNeeded = async () => {
    if (isAuthenticated.value && !notifications.loaded) {
        await notifications.load();
    }
};

onMounted(loadNotificationsIfNeeded);

watch(isAuthenticated, () => {
    void loadNotificationsIfNeeded();
});

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_app_bar = _resolveComponent("v-app-bar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_app_bar, {
      flat: "",
      height: "72",
      class: _normalizeClass(["app-header", { 'app-header--page-cta': _unref(headerCta) }])
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_RouterLink, {
            to: isAuthenticated.value ? '/dashboard' : '/',
            class: "app-header__logo",
            "aria-label": "Перейти на главную"
          }, {
            default: _withCtx(() => [
              _createVNode(SvgLogo, { width: logoWidth.value }, null, 8, ["width"])
            ]),
            _: 1
          }, 8, ["to"])
        ]),
        (hasCenterContent.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              (_unref(headerCta))
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: _normalizeClass(["app-header__page-cta", `app-header__page-cta--${_unref(headerCta).tone ?? 'violet'}`]),
                    "data-testid": _unref(headerCta).testId,
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(headerCta).onClick()))
                  }, [
                    _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(headerCta).label), 1)
                  ], 10, _hoisted_4))
                : (showBecomeMentorCta.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      class: "app-header__cta",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (handleNavigate('/become-mentor')))
                    }, _cache[7] || (_cache[7] = [
                      _createElementVNode("span", {
                        class: "app-header__cta-icon",
                        "aria-hidden": "true"
                      }, [
                        _createElementVNode("svg", {
                          width: "20",
                          height: "20",
                          viewBox: "0 0 26 26",
                          fill: "none",
                          xmlns: "http://www.w3.org/2000/svg"
                        }, [
                          _createElementVNode("path", {
                            d: "M4.44444 2V6.88889M2 4.44444H6.88889M5.66667 19.1111V24M3.22222 21.5556H8.11111M14.2222 2L17.0159 10.381L24 13L17.0159 15.619L14.2222 24L11.4286 15.619L4.44444 13L11.4286 10.381L14.2222 2Z",
                            stroke: "currentColor",
                            "stroke-width": "2.04545",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          })
                        ])
                      ], -1),
                      _createTextVNode(" Стать наставником ")
                    ])))
                  : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true),
        (hasRightCluster.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              (isAuthenticated.value && userRole.value)
                ? (_openBlock(), _createBlock(UserRoleChips, {
                    key: 0,
                    class: _normalizeClass(["app-header__role-chips", { 'app-header__role-chips--mobile': isMobile.value }]),
                    "current-role": userRole.value === 'mentor' ? 'mentor' : 'student',
                    "application-phase": mentorAppPhase.value,
                    "rejection-reason": mentorAppRejectionReason.value,
                    "payout-missing": payoutMissing.value,
                    compact: chipCompact.value,
                    onClickMentorChip: onMentorChipClick,
                    onClickPayoutAttach: onPayoutAttachClick
                  }, null, 8, ["class", "current-role", "application-phase", "rejection-reason", "payout-missing", "compact"]))
                : _createCommentVNode("", true),
              _createVNode(NotificationComponent, {
                class: "app-header__bell",
                "avatar-url": avatarUrl.value,
                "notification-count": _unref(notifications).unreadCount,
                "is-authenticated": isAuthenticated.value,
                onLoginClick: _cache[2] || (_cache[2] = ($event: any) => (handleNavigate('/login')))
              }, null, 8, ["avatar-url", "notification-count", "is-authenticated"]),
              (isAuthenticated.value && !isMobile.value)
                ? (_openBlock(), _createBlock(_component_v_menu, {
                    key: 1,
                    modelValue: profileMenuOpen.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((profileMenuOpen).value = $event)),
                    "close-on-content-click": false,
                    transition: "fade-transition",
                    "transition-duration": 300
                  }, {
                    activator: _withCtx(({ props }) => [
                      _createElementVNode("button", _mergeProps({ class: "app-header__profile" }, props, { "aria-label": "Открыть меню профиля" }), [
                        _createVNode(UserAvatar, {
                          user: user.value,
                          size: 48,
                          alt: "Профиль",
                          class: "app-header__profile-image"
                        }, null, 8, ["user"])
                      ], 16)
                    ]),
                    default: _withCtx(() => [
                      _createVNode(ProfileMenu, {
                        onClose: _cache[3] || (_cache[3] = ($event: any) => (profileMenuOpen.value = false))
                      })
                    ]),
                    _: 1
                  }, 8, ["modelValue"]))
                : _createCommentVNode("", true),
              (isMobile.value && isAuthenticated.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 2,
                    class: "app-header__profile app-header__profile--mobile",
                    "aria-label": "Открыть меню",
                    onClick: toggleDrawer
                  }, [
                    _createVNode(UserAvatar, {
                      user: user.value,
                      size: 48,
                      alt: "Профиль",
                      class: "app-header__profile-image"
                    }, null, 8, ["user"])
                  ]))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["class"]),
    (isMobile.value && isAuthenticated.value)
      ? (_openBlock(), _createBlock(HeaderMobileDrawer, {
          key: 0,
          open: mobileDrawer.value,
          "onUpdate:open": _cache[5] || (_cache[5] = ($event: any) => ((mobileDrawer).value = $event)),
          "is-authenticated": isAuthenticated.value,
          "user-name": userName.value,
          "user-email": userEmail.value,
          scrim: "rgba(25, 21, 45, 0.35)",
          onNavigate: handleNavigate,
          onLogout: handleLogout
        }, null, 8, ["open", "is-authenticated", "user-name", "user-email"]))
      : _createCommentVNode("", true),
    _createVNode(LogoutConfirmDialog, {
      modelValue: showLogoutConfirm.value,
      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showLogoutConfirm).value = $event)),
      loading: logoutLoading.value,
      onConfirm: confirmLogout
    }, null, 8, ["modelValue", "loading"])
  ]))
}
}

})